import { UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DateRange } from "react-day-picker";

import {
  decisionApi,
  ExporterMetricsEndpoint,
  GetDecisionSummaryParams,
} from "src/api/endpoints";
import { GenericObject } from "src/api/flowTypes";
import { DecisionEnvironment, DecisionSummaryResponse } from "src/api/types";
import { useIsIcebergDecisionSummaryEnabled } from "src/hooks/useIsIcebergDecisionSummaryEnabled";

const decisionsOverviewKeys = {
  all: (baseUrl: string, params: GenericObject, isIcebergEnabled: boolean) =>
    [
      "decision-overview",
      baseUrl,
      JSON.stringify(params),
      isIcebergEnabled,
    ] as const,
};

export const useDecisionsOverview = (
  baseUrl: string | undefined,
  params: Omit<
    GetDecisionSummaryParams,
    "start_start_time" | "end_start_time"
  > & {
    timeWindow?: DateRange;
  },
  options?: Pick<
    UseQueryOptions<DecisionSummaryResponse, AxiosError>,
    "enabled" | "retry"
  > & {
    icebergOverride?: boolean;
  },
) => {
  const isIcebergDecisionSummaryEnabled = useIsIcebergDecisionSummaryEnabled();
  const { icebergOverride, ...queryOptions } = options ?? {};
  const iceberg = icebergOverride ?? isIcebergDecisionSummaryEnabled;

  return useQuery<DecisionSummaryResponse, AxiosError>({
    queryKey: decisionsOverviewKeys.all(baseUrl!, params, iceberg),
    queryFn: () =>
      ExporterMetricsEndpoint.getDecisionSummary(
        baseUrl!,
        {
          start_start_time: params.timeWindow?.from?.toISOString(),
          end_start_time: params.timeWindow?.to?.toISOString(),
          ...params,
        },
        iceberg,
      ),
    ...queryOptions,
    enabled: Boolean(baseUrl) && queryOptions?.enabled !== false,
  });
};

export const useCancelDecision = (
  baseUrl: string,
  environment: DecisionEnvironment,
) => {
  const newDecisionApi = decisionApi(baseUrl);
  return useMutation({
    mutationFn: async ({
      flowSlug,
      decisionId,
    }: {
      flowSlug: string;
      decisionId: string;
    }) => {
      const params = { flowSlug, decisionId };
      if (environment === DecisionEnvironment.LIVE) {
        return newDecisionApi.cancelPendingDecisionRunApiV1FlowsFlowSlugDecideDecisionIdCancelPost(
          params,
        );
      }
      return newDecisionApi.cancelPendingDecisionSandboxRunApiV1FlowsFlowSlugSandboxDecideDecisionIdCancelPost(
        params,
      );
    },
  });
};
